import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type {
  InvoiceExtractionOut,
  InvoiceLinkOut,
  TransactionDocumentsOut,
} from '../api/invoices';
import { listTransactionDocuments } from '../api/invoices';
import { useAuth } from '../auth/AuthContext';
import { requiredInvoiceFieldsComplete } from '../lib/invoiceCompleteness';

/** Review-queue invoice filter: '' = any; attachment presence; or, for an
 *  attached invoice, whether its required fields are captured (`complete`
 *  = green check on the chip, `incomplete` = purple question mark). */
export type InvoiceFilter =
  | ''
  | 'attached'
  | 'unattached'
  | 'no_invoice'
  | 'complete'
  | 'incomplete';

/** Hard cap on ids per transaction-documents request. External
 *  transaction ids ride as repeated query params, so a run with 1K+
 *  unique transactions must split across requests. */
export const DOCS_CHUNK_SIZE = 150;

/** Max encoded query-string budget per chunk. Xero transaction ids
 *  (`X:{date}:{contact}:{invoice}`) are long — 150 of them blew past the
 *  server URL/header limit and returned HTTP 431, which failed the whole
 *  (Promise.all) lookup and disabled the invoice filter. Chunk by
 *  cumulative encoded length as well as count so no chunk ever exceeds
 *  the limit regardless of id length; short NVG ids still pack ~150 each. */
const DOCS_CHUNK_MAX_QUERY_CHARS = 6000;
// `transaction_ids=` (16) + one `&` separator per id.
const _PARAM_OVERHEAD = 17;

/** Stable de-duplicated id list — sorted so the query key (and thus the
 *  cache entry) is identical regardless of finding order. */
export function dedupeTransactionIds(transactionIds: string[]): string[] {
  return [...new Set(transactionIds)].filter((id) => id.length > 0).sort();
}

export function chunkTransactionIds(
  ids: string[],
  size: number = DOCS_CHUNK_SIZE,
): string[][] {
  const chunks: string[][] = [];
  let current: string[] = [];
  let currentChars = 0;
  for (const id of ids) {
    const cost = encodeURIComponent(id).length + _PARAM_OVERHEAD;
    if (
      current.length > 0 &&
      (current.length >= Math.min(size, DOCS_CHUNK_SIZE) ||
        currentChars + cost > DOCS_CHUNK_MAX_QUERY_CHARS)
    ) {
      chunks.push(current);
      current = [];
      currentChars = 0;
    }
    current.push(id);
    currentChars += cost;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

/** Merge the per-chunk responses into one ext-transaction-id → links map.
 *  Chunks never overlap, so a plain last-write-wins set is safe. */
export function mergeLinkPages(
  pages: TransactionDocumentsOut[],
): Map<string, InvoiceLinkOut[]> {
  const merged = new Map<string, InvoiceLinkOut[]>();
  for (const page of pages) {
    for (const [transactionId, links] of Object.entries(page.links)) {
      merged.set(transactionId, links);
    }
  }
  return merged;
}

/** Merge the per-chunk "no invoice available" marks into one set of
 *  external transaction ids. */
export function mergeMarkPages(pages: TransactionDocumentsOut[]): Set<string> {
  const marked = new Set<string>();
  for (const page of pages) {
    for (const id of page.no_invoice_marks ?? []) marked.add(id);
  }
  return marked;
}

/** Merge the per-chunk extraction maps (document id -> extraction). */
export function mergeExtractionPages(
  pages: TransactionDocumentsOut[],
): Map<string, InvoiceExtractionOut> {
  const merged = new Map<string, InvoiceExtractionOut>();
  for (const page of pages) {
    for (const [documentId, extraction] of Object.entries(page.extractions)) {
      merged.set(documentId, extraction);
    }
  }
  return merged;
}

/** Merge the per-chunk presigned download-URL maps (document id -> URL).
 *  Only populated when the request set `withDownload` (the finding detail
 *  page); empty otherwise. */
export function mergeDownloadUrlPages(
  pages: TransactionDocumentsOut[],
): Map<string, string> {
  const merged = new Map<string, string>();
  for (const page of pages) {
    for (const [documentId, url] of Object.entries(page.download_urls ?? {})) {
      merged.set(documentId, url);
    }
  }
  return merged;
}

/** The invoice-filter predicate. A transaction absent from the map has
 *  no links — `undefined` and `[]` both mean unattached. */
export function invoiceFilterAccepts(
  filter: InvoiceFilter,
  links: InvoiceLinkOut[] | undefined,
  isMarked = false,
  extractions?: Map<string, InvoiceExtractionOut>,
): boolean {
  if (filter === '') return true;
  const attached = links !== undefined && links.length > 0;
  if (filter === 'attached') return attached;
  // Data-completeness of an attached invoice — matches the chip's green
  // check / purple question mark. Both require an attachment: an
  // unattached transaction is neither complete nor incomplete.
  if (filter === 'complete') return requiredInvoiceFieldsComplete(links, extractions);
  if (filter === 'incomplete')
    return attached && !requiredInvoiceFieldsComplete(links, extractions);
  // "No invoice available" is a triaged marker; "unattached" is the
  // still-to-collect set, so a marked transaction is not unattached.
  if (filter === 'no_invoice') return isMarked;
  return !attached && !isMarked; // 'unattached'
}

/** Batched invoice-link lookup for a set of findings' transactions.
 *
 *  One chunked fetch per unique-id set (not per finding — the N+1 shape
 *  this exists to avoid), resolved via `(customer_id, transaction_id)`
 *  so an invoice assigned on any run surfaces on every run. Disabled
 *  until a customer slug exists: CLI-triggered runs have no customer
 *  context and simply render no chips.
 */
export function useTransactionDocuments(
  customerSlug: string | null,
  transactionIds: string[],
  opts: { withDownload?: boolean } = {},
): {
  linksByTransaction: Map<string, InvoiceLinkOut[]>;
  extractionsByDocument: Map<string, InvoiceExtractionOut>;
  downloadUrlsByDocument: Map<string, string>;
  markedTransactions: Set<string>;
  linksReady: boolean;
  linksError: boolean;
} {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const ids = useMemo(() => dedupeTransactionIds(transactionIds), [transactionIds]);
  // Single-page callers (the finding detail page) opt into bundled presigned
  // URLs so the invoice image loads without a second round-trip; the run-page
  // batch usage leaves it off. Part of the query key so the two callers don't
  // share a cache entry that lacks the URLs.
  const withDownload = opts.withDownload ?? false;

  const query = useQuery({
    queryKey: ['transaction-documents', customerSlug, userKey, ids.join(','), withDownload],
    enabled: isSignedIn && customerSlug !== null && ids.length > 0,
    queryFn: async () => {
      const token = (await getIdToken()) ?? '';
      const slug = customerSlug ?? '';
      return Promise.all(
        chunkTransactionIds(ids).map((chunk) =>
          listTransactionDocuments(slug, chunk, token, withDownload),
        ),
      );
    },
  });

  const linksByTransaction = useMemo(
    () => (query.data ? mergeLinkPages(query.data) : new Map<string, InvoiceLinkOut[]>()),
    [query.data],
  );
  const extractionsByDocument = useMemo(
    () =>
      query.data ? mergeExtractionPages(query.data) : new Map<string, InvoiceExtractionOut>(),
    [query.data],
  );
  const downloadUrlsByDocument = useMemo(
    () => (query.data ? mergeDownloadUrlPages(query.data) : new Map<string, string>()),
    [query.data],
  );
  const markedTransactions = useMemo(
    () => (query.data ? mergeMarkPages(query.data) : new Set<string>()),
    [query.data],
  );

  // An errored lookup must not masquerade as "no attachments" — callers
  // use linksError to disable the has-invoice filter and warn, instead
  // of silently filtering on an empty map.
  return {
    linksByTransaction,
    extractionsByDocument,
    downloadUrlsByDocument,
    markedTransactions,
    linksReady: query.data !== undefined,
    linksError: query.isError,
  };
}
